import * as THREE from 'three';
import { Signal } from './Signal.js';
import { Peer } from './Peer.js';
import { RemotePlayer } from './RemotePlayer.js';
import { encodeSnapshot, decodeSnapshot, encodeInput, decodeInput, FLAG } from './protocol.js';
import { TEAM, MEANS } from '../game/MatchAuthority.js';

/**
 * The multiplayer session: who is connected, who decides, and what crosses the wire.
 *
 * ## Star, not mesh
 *
 * Every client holds exactly one connection, to the host. A full mesh would be 28
 * connections at eight players against 7, and — far more importantly — a mesh has
 * no centre, so there would be nowhere for the authority to live. The host is that
 * centre for both reasons at once.
 *
 * ## Where each decision is made
 *
 * | Question | Decided by | Why |
 * |---|---|---|
 * | Where am I standing? | each player, locally | Movement must answer the key you just pressed. A 60 ms round trip before you move is unplayable, and it is the one thing a friend has no reason to lie about. |
 * | Did my shot hit? | the shooter's machine | You shoot at what your screen shows you. Re-running the ray on the host against a different history means the hits you *saw* land do not count. |
 * | How much did it hurt, and did it kill? | **the host, always** | Two machines that disagree about a death cannot recover. This is the whole reason `MatchAuthority` was extracted before any of this was written. |
 *
 * That middle row is the honest compromise of a friends-only game: a modified
 * client could claim a hit it did not make. Closing it needs the host to keep a
 * second of position history and re-simulate the shot against it, which is real
 * work and buys nothing when the four people in the room chose to be there
 * together. It is written down here so that the day this opens to strangers,
 * nobody has to rediscover which row is the soft one.
 *
 * ## What is not synchronised yet
 *
 * Bots. The AI director runs on every machine and would produce different
 * soldiers on each, so it is switched off in a networked match and the humans
 * fight each other. Saying that plainly is better than shipping bots that
 * teleport.
 */

export const TUNING = {
  /** Snapshots per second. 20 is the genre standard and interpolation hides it. */
  tickHz: 20,
  maxPlayers: 8,
};

const _v = new THREE.Vector3();

export class NetSession {
  constructor(ctx) {
    this.ctx = ctx;
    this.signal = null;
    this.peers = new Map(); // peerId -> Peer
    this.remotes = new Map(); // slot -> RemotePlayer
    this.slotOf = new Map(); // peerId -> slot
    this.inputs = new Map(); // slot -> latest decoded input (host only)
    this.names = new Map(); // peerId -> name

    this.active = false;
    this.isHost = false;
    this.code = null;
    this.mySlot = 0;
    this.tick = 0;
    this._acc = 0;
    this._localTeam = TEAM.ALPHA;

    ctx.engine.registerUpdate((dt) => this.update(dt), 'ui');

    /**
     * Our own gunfire, sent so the others see a flash and hear a report from the
     * right place. Deliberately not the round: that was resolved here and its
     * damage travels as `dmg`, so a lost `fire` costs a muzzle flash and nothing
     * that decides anything.
     */
    ctx.events.on('weapon:fire', (e) => {
      if (!this.active || !e?.origin) return;
      this.broadcast({ t: 'fire', slot: this.mySlot, origin: e.origin.toArray() });
    });
  }

  /* -------------------------------- lifecycle ------------------------------- */

  async host(name = 'HOST') {
    const sig = new Signal();
    const code = await sig.createCode();
    await this._start(sig, code, name);
    return code;
  }

  async join(code, name = 'PLAYER') {
    const sig = new Signal();
    await this._start(sig, code, name);
    return code;
  }

  async _start(sig, code, name) {
    this.signal = sig;
    this.code = code;
    this.name = name;

    sig.addEventListener('peer:join', (e) => this._onPeerJoin(e.detail));
    sig.addEventListener('peer:leave', (e) => this._onPeerLeave(e.detail));
    sig.addEventListener('offer', (e) => this._onOffer(e.detail));
    sig.addEventListener('answer', (e) => this._onAnswer(e.detail));
    sig.addEventListener('ice', (e) => this._onIce(e.detail));
    sig.addEventListener('room:closed', () => this.leave('host left'));

    const welcome = await sig.connect(code, name);
    // Everyone already in the room, named. Without this the peer list shows a
    // joiner the placeholder "PLAYER" for people who were here before them —
    // `peer:join` only ever fires for arrivals *after* you.
    for (const p of welcome.peers ?? []) this.names.set(p.id, p.name);
    this.isHost = !!welcome.host;
    this.active = true;
    this.mySlot = 0;

    /**
     * Bots off, and off means gone. The director is deterministic per machine but
     * not *across* machines — same seed, different frame timings — so every player
     * would see a different squad standing in different places. Humans only, and
     * said out loud in the UI rather than left as a surprise.
     *
     * `setEnabled(false)` rather than the `enabled` flag: the flag stops spawning
     * and leaves the existing squad fighting, which was measured shooting a remote
     * player and scoring for a team while both humans stood still.
     */
    this.ctx.ai?.setEnabled(false);

    if (this.isHost) {
      this._slotSeq = 1;
      this._localTeam = TEAM.ALPHA;
      // Existing peers offer to newcomers, and in a star the host is the only
      // existing peer that matters.
      for (const p of welcome.peers ?? []) this._connectTo(p.id, true);
    } else {
      this._localTeam = TEAM.BRAVO;
    }

    this._emitState();
    return welcome;
  }

  leave(reason = 'left') {
    if (!this.active) return;
    this.active = false;
    for (const p of this.peers.values()) p.close();
    this.peers.clear();
    for (const r of this.remotes.values()) r.dispose();
    this.remotes.clear();
    this.slotOf.clear();
    this.inputs.clear();
    this.signal?.close();
    this.signal = null;
    this.ctx.ai?.setEnabled(true);
    this.ctx.events.emit('net:closed', { reason });
    this._emitState();
  }

  /* ------------------------------- signalling ------------------------------- */

  _onPeerJoin({ id, name }) {
    this.names.set(id, name);
    // Star topology: only the host dials. A client hearing about another client
    // must do nothing, or every pair would negotiate a connection nobody uses.
    if (this.isHost) this._connectTo(id, true);
    this._emitState();
  }

  _onPeerLeave({ id }) {
    this.peers.get(id)?.close();
    this.peers.delete(id);
    const slot = this.slotOf.get(id);
    if (slot != null) {
      this.remotes.get(slot)?.dispose();
      this.remotes.delete(slot);
      this.inputs.delete(slot);
      this.slotOf.delete(id);
    }
    this.names.delete(id);
    this._emitState();
  }

  _connectTo(id, initiator) {
    if (this.peers.has(id)) return this.peers.get(id);
    const peer = new Peer(id, !initiator, (m) => this.signal?.send(m));
    this.peers.set(id, peer);

    peer.addEventListener('open', () => {
      if (this.isHost) {
        // The host owns the slot table, so it hands one out and says who else is
        // here in the same breath.
        const slot = this._slotSeq++;
        this.slotOf.set(id, slot);
        this._ensureRemote(slot, this.names.get(id) ?? 'PLAYER', TEAM.BRAVO);
        peer.sendEvent({
          t: 'assign',
          slot,
          team: TEAM.BRAVO,
          host: { slot: 0, team: TEAM.ALPHA, name: this.name },
          peers: [...this.slotOf.entries()]
            .filter(([pid]) => pid !== id)
            .map(([pid, s]) => ({ slot: s, name: this.names.get(pid) ?? 'PLAYER', team: TEAM.BRAVO })),
        });
      }
      this._emitState();
    });

    peer.addEventListener('closed', () => {
      this.peers.delete(id);
      this._onPeerLeave({ id });
    });

    peer.addEventListener('state:data', (e) => this._onStateData(id, e.detail));
    peer.addEventListener('event:data', (e) => this._onEventData(id, e.detail));

    if (initiator) peer.offer().catch((err) => console.warn('[net] offer failed', err));
    return peer;
  }

  async _onOffer({ from, data }) {
    const peer = this._connectTo(from, false);
    await peer.onOffer(data);
  }

  async _onAnswer({ from, data }) {
    await this.peers.get(from)?.onAnswer(data);
  }

  async _onIce({ from, data }) {
    await this.peers.get(from)?.onIce(data);
  }

  /* --------------------------------- players -------------------------------- */

  _ensureRemote(slot, name, team) {
    let r = this.remotes.get(slot);
    if (r) return r;
    r = new RemotePlayer(this.ctx, { slot, name, team });
    this.remotes.set(slot, r);
    return r;
  }

  _localFlags() {
    const p = this.ctx.player;
    let f = 0;
    if (p.crouching) f |= FLAG.CROUCH;
    if (p.ads) f |= FLAG.ADS;
    if (p.sprinting) f |= FLAG.SPRINT;
    if (p.dead) f |= FLAG.DEAD;
    if (p.grounded) f |= FLAG.GROUNDED;
    return f;
  }

  /* ---------------------------------- frame --------------------------------- */

  update(dt) {
    if (!this.active) return;
    const now = performance.now();

    for (const r of this.remotes.values()) r.update(dt, now);

    this._acc += dt;
    const step = 1 / TUNING.tickHz;
    if (this._acc < step) return;
    this._acc %= step;
    this.tick++;

    const p = this.ctx.player;
    if (!p) return;

    if (this.isHost) {
      const players = [{
        slot: 0,
        x: p.position.x, y: p.position.y, z: p.position.z,
        yaw: p.yaw ?? 0, pitch: p.pitch ?? 0,
        flags: this._localFlags(),
        health: p.health ?? 0,
      }];
      for (const [slot, r] of this.remotes) {
        players.push({
          slot,
          x: r.position.x, y: r.position.y, z: r.position.z,
          yaw: r.yaw, pitch: r.pitch,
          flags: (r.crouching ? FLAG.CROUCH : 0) | (r.ads ? FLAG.ADS : 0) | (r.dead ? FLAG.DEAD : 0),
          health: r.health,
        });
      }
      const buf = encodeSnapshot(this.tick, players);
      for (const peer of this.peers.values()) peer.sendState(buf);
    } else {
      const buf = encodeInput(this.tick, {
        x: p.position.x, y: p.position.y, z: p.position.z,
        yaw: p.yaw ?? 0, pitch: p.pitch ?? 0,
        flags: this._localFlags(),
      });
      for (const peer of this.peers.values()) peer.sendState(buf);
    }
  }

  _onStateData(fromId, data) {
    const buf = data instanceof ArrayBuffer ? data : null;
    if (!buf || buf.byteLength < 6) return;
    const now = performance.now();

    if (this.isHost) {
      const input = decodeInput(buf);
      if (!input) return;
      const slot = this.slotOf.get(fromId);
      if (slot == null) return;
      const r = this.remotes.get(slot);
      if (!r) return;
      /**
       * The client's own position, taken as given. The host owns damage and
       * death; it does not own where a friend is standing. See the table at the
       * top of this file for what that costs and why it is worth it here.
       */
      r.push(input, now);
      this.inputs.set(slot, input);
    } else {
      const snap = decodeSnapshot(buf);
      if (!snap) return;
      for (const s of snap.players) {
        if (s.slot === this.mySlot) {
          // Our own row, authored by the host: health and death only. Position is
          // ours — accepting it back would be a rubber band at 20 Hz.
          this._applyLocalAuthority(s);
          continue;
        }
        const r = this._ensureRemote(s.slot, `P${s.slot}`, s.slot === 0 ? TEAM.ALPHA : TEAM.BRAVO);
        r.push(s, now);
        r.health = s.health;
      }
    }
  }

  /**
   * The host's verdict on our own body: health and death, never position.
   *
   * Guarded by a grace window after a local respawn. Respawning is decided on this
   * machine — same trust model as movement — so for the tick or two before the
   * host has seen our DEAD flag clear, its snapshots still carry `health: 0` and
   * a dead flag. Applying those would kill the player the instant they came back,
   * once, unpredictably, and only over a real network where the round trip is long
   * enough to notice.
   */
  _applyLocalAuthority(s) {
    const p = this.ctx.player;
    if (!p) return;
    if (performance.now() < (this._respawnGraceUntil ?? 0)) return;
    const dead = !!(s.flags & FLAG.DEAD);
    if (s.health !== p.health) {
      p.health = s.health;
      this.ctx.events.emit('player:health', { health: s.health, max: 100 });
    }
    if (dead && !p.dead) p.applyDeath();
  }

  /** Called when this machine puts its own player back on the map. */
  noteLocalRespawn() {
    this._respawnGraceUntil = performance.now() + 1000;
  }

  /* --------------------------------- events --------------------------------- */

  _onEventData(fromId, raw) {
    let m;
    try {
      m = JSON.parse(raw);
    } catch {
      return;
    }
    switch (m.t) {
      case 'assign': {
        this.mySlot = m.slot;
        this._localTeam = m.team;
        this._ensureRemote(m.host.slot, m.host.name ?? 'HOST', m.host.team);
        for (const q of m.peers ?? []) this._ensureRemote(q.slot, q.name, q.team);
        this._emitState();
        return;
      }

      /**
       * A damage claim from a client. The host does not take the number on trust —
       * it feeds it through the same `requestDamage` its own shots go through, so
       * friendly fire, splash rules, hitbox multipliers and kill credit are decided
       * once, by the mode, for everybody.
       */
      case 'dmg': {
        if (!this.isHost) return;
        const slot = this.slotOf.get(fromId);
        const source = slot != null ? this.remotes.get(slot) : null;
        const target = this._actorForSlot(m.target);
        if (!source || !target) return;
        this.ctx.match?.requestDamage({
          target,
          source,
          amount: m.amount,
          hitbox: target.hitboxes?.find((h) => h.tag === m.hitbox) ?? null,
          point: m.point ? _v.fromArray(m.point).clone() : null,
          dir: m.dir ? new THREE.Vector3().fromArray(m.dir) : null,
          means: m.means ?? MEANS.BULLET,
          weapon: m.weapon ?? null,
        });
        return;
      }

      case 'fire': {
        // Cosmetic only: the muzzle flash and the report. The round itself was
        // resolved on the shooter's machine and its damage arrives as `dmg`.
        this.ctx.events.emit('audio:play', {
          id: 'enemy_fire',
          position: new THREE.Vector3().fromArray(m.origin),
          volume: 0.8,
        });
        const slot = this.slotOf.get(fromId) ?? m.slot;
        this.remotes.get(slot)?.model.fireFlash?.();
        return;
      }

      case 'death': {
        // Host → clients. The client applies it; it never decides it.
        if (this.isHost) return;
        const victim = this._actorForSlot(m.slot);
        if (victim && victim !== this.ctx.player) victim.applyDeath?.(m);
        this.ctx.events.emit('actor:death', {
          actor: victim,
          source: this._actorForSlot(m.by),
          headshot: !!m.headshot,
          means: m.means,
          team: m.team,
          sourceTeam: m.byTeam,
          mine: m.by === this.mySlot,
        });
        return;
      }

      case 'respawn': {
        if (this.isHost) return;
        const who = this._actorForSlot(m.slot);
        const pos = new THREE.Vector3().fromArray(m.pos);
        if (who === this.ctx.player) this.ctx.player.respawn(pos);
        else who?.respawn?.(pos);
        return;
      }

      default:
        return;
    }
  }

  _actorForSlot(slot) {
    if (slot === this.mySlot) return this.ctx.player;
    return this.remotes.get(slot) ?? null;
  }

  /**
   * A client's damage request, on its way to the host.
   *
   * Returns a result shaped like a real one so the shooter gets their hitmarker
   * this frame, marked `pending` so nothing downstream mistakes it for applied
   * damage. Returns null when there is nobody to ask, which drops the request back
   * into the local path — the correct behaviour for a shot at a bot in a match
   * that has not finished connecting.
   */
  forwardDamage(req) {
    // `req.target` may be an actor or an id, exactly as `requestDamage` accepts it.
    const actor = this.ctx.match?.get(req.target)?.actor ?? null;
    const target = this.slotForActor(actor);
    if (target < 0) return null;
    const scale = req.hitbox?.damageScale ?? 1;
    this.toHost({
      t: 'dmg',
      target,
      amount: req.amount ?? 0,
      hitbox: req.hitbox?.tag ?? null,
      point: req.point ? req.point.toArray() : null,
      dir: req.dir ? req.dir.toArray() : null,
      means: req.means,
      weapon: req.weapon ?? null,
    });
    return {
      pending: true,
      blocked: false,
      amount: (req.amount ?? 0) * scale,
      headshot: req.hitbox?.tag === 'head',
      fatal: false,
      hitbox: req.hitbox ?? null,
      dir: req.dir ?? null,
      point: req.point ?? null,
      means: req.means,
      weapon: req.weapon ?? null,
    };
  }

  /** Host only: tell every client who died, and to whom. */
  announceDeath(resolved) {
    if (!this.active || !this.isHost) return;
    const slot = this.slotForActor(resolved.target.actor);
    if (slot < 0) return;
    this.broadcast({
      t: 'death',
      slot,
      by: this.slotForActor(resolved.source?.actor ?? null),
      byTeam: resolved.source?.team ?? TEAM.NONE,
      team: resolved.target.team,
      headshot: resolved.headshot,
      means: resolved.means,
    });
  }

  /** Slot of an actor, for putting one on the wire. */
  slotForActor(actor) {
    if (!actor) return -1;
    if (actor === this.ctx.player) return this.mySlot;
    for (const [slot, r] of this.remotes) if (r === actor) return slot;
    return -1;
  }

  /* ------------------------------ outbound API ------------------------------ */

  /** Broadcast to everyone we hold a connection to. */
  broadcast(obj) {
    for (const peer of this.peers.values()) peer.sendEvent(obj);
  }

  /** Send only to the host — used by clients for anything the host decides. */
  toHost(obj) {
    if (this.isHost) return false;
    for (const peer of this.peers.values()) return peer.sendEvent(obj);
    return false;
  }

  _emitState() {
    this.ctx.events.emit('net:state', {
      active: this.active,
      host: this.isHost,
      code: this.code,
      slot: this.mySlot,
      team: this._localTeam,
      peers: [...this.peers.values()].map((p) => ({
        id: p.id,
        name: this.names.get(p.id) ?? 'PLAYER',
        open: p.open,
      })),
    });
  }
}
